import React, { FunctionComponent } from 'react';
import { map, last, prepend, all } from 'ramda';
import { FormattedMessage } from 'react-intl';
import RegularButton from '../../../../components/RegularButton';
import { IBindingAction } from '../../../../common/models/callback';
import { IRefReqCounters } from '../../../../common/models/referenceRequest/IRefReqCounters';
import { ReactComponent as ArrowRight } from '../../../../assets/icons/arrow-right.svg';
import { ReactComponent as ArrowLeft } from '../../../../assets/icons/arrow-left.svg';
import { getReferenceProgress } from '../../../../components/ReferenceRoleForm/utils';
import { IReferenceCandidateForm } from '../../../../common/models/reference/IReferenceCandidateForm';
import { messages } from '../Overview/utils';
import IconButton from '../../../../components/IconButton';
import { ReferenceType } from '../../../../common/enums/ReferenceType';
import Icon from '../../../../components/Icon';
import styles from './styles.module.scss';
import { useSwipeable } from 'react-swipeable';

export interface IFinalProps {
  counters: IRefReqCounters;
  currentReference: IReferenceCandidateForm;
  references: IReferenceCandidateForm[];
  onGoBack: IBindingAction;
  onFinish: IBindingAction;
  onAddMore: IBindingAction;
}

const Final: FunctionComponent<IFinalProps> = ({
  counters,
  currentReference,
  references,
  onGoBack,
  onFinish,
  onAddMore
}) => {
  const refs = currentReference.id ? [...references, currentReference] : references;
  const count = prepend(
    { total: counters.totalCount, provided: refs.length, type: 'total' },
    getReferenceProgress(refs, counters)
  );
  const allFilledIn = all(({ provided, total }) => provided >= total, count);

  const swipeHandler = useSwipeable({
    onSwipedLeft: allFilledIn ? onFinish : onAddMore,
    onSwipedRight: onGoBack,
    trackMouse: false
  });

  return (
    <div {...swipeHandler} className="cf-container w-100">
      <p className={`cf-title ${styles.finalTitle}`}>
        <FormattedMessage
          id="candidateForm.final.title"
          defaultMessage="Great, {firstName} has been added!"
          values={{ firstName: last(refs)?.firstName }}
        />
      </p>
      <div className="cf-subtitle mb-3">
        <FormattedMessage id="candidateForm.final.subtitle" defaultMessage="Your progress so far:" />
      </div>
      <div className={styles.itemsContainer}>
        {map(({ type, provided, total }) => {
          const { id, defaultMessage } = messages[type as ReferenceType | 'total'];
          return (
            <div className={`${(total <= provided && total) ? styles.success : ''} ${styles.item}`} key={type}>
              <div>
                {provided}
                &nbsp;
                <FormattedMessage id={`${id}.${provided === 1 ? 'one' : 'many'}`} defaultMessage={defaultMessage} />
              </div>
              {Boolean(total) && (
                <div className={styles.counters}>
                  {provided}
                  &nbsp;of&nbsp;
                  {total}
                </div>
              )}
            </div>
          );
        }, count)}
      </div>
      <div className="cf-footer-btns">
        <IconButton
          size="lg"
          icon={<ArrowLeft />}
          onClick={onGoBack}
        />
        <div className={styles.finalBttns}>
          {allFilledIn && (
            <RegularButton
              size="lg"
              textIntlId="candidateForm.addExtra"
              defaultMessage="Add extra reference"
              onClick={onAddMore}
              variant="secondary"
            />
          )}
          <RegularButton
            size="lg"
            variant={allFilledIn ? 'primary' : 'tertiary'}
            textIntlId="candidateForm.finish"
            defaultMessage="Finish"
            onClick={onFinish}
            iconPosition="right"
            icon={allFilledIn ? <Icon icon="arrowRight" /> : undefined}
          />
          {!allFilledIn && (
            <RegularButton
              size="lg"
              textIntlId="candidateForm.addNext"
              defaultMessage="Add next reference"
              icon={<ArrowRight />}
              iconPosition="right"
              onClick={onAddMore}
            />
          )}
        </div>

      </div>
    </div>
  );
};

export default Final;
